<script setup lang="ts">
import { computed, inject, ref, watch } from "vue";
import { nameInjectionKey } from "./../keys";
import { ValidationArgs, useVuelidate } from "@vuelidate/core";
import { CurrencyDisplay, useCurrencyInput } from "vue-currency-input";

const props = withDefaults(defineProps<{
    type: "text" | "email" | "number" | "currency",
    modelValue: string | number | null,
    hidePii?: boolean,
    inputmode?: "numeric" | "text" | undefined,
    hiddenLabel?: string | undefined,
    label?: string | undefined,
    placeholder?: string | null,
    class?: string | null,
    inputClass?: string | null,
    maxLength?: number | undefined,
    filter?: (e: KeyboardEvent) => boolean | undefined,
    append?: string,
    prepend?: string,
    id?: string,
    validation?: ValidationArgs,
    emitBlurEvent?: boolean,
    disabled?: boolean,
    reactiveValidation?: boolean,
}>(), {
    hidePii: true,
    filter: () => true,
    maxLength: 999,
    inputmode: undefined,
    placeholder: undefined,
    label: undefined,
    hiddenLabel: undefined,
    class: "col-md-6",
    inputClass: "",
    append: "",
    prepend: "",
    suggestions: undefined,
    id: undefined,
    validation: undefined,
    emitBlurEvent: false,
    disabled: false,
    reactiveValidation: false,
});

const internalModelValue = computed(() => props.modelValue);
const v$ = props.reactiveValidation ? runReactiveValidation() : runStaticValidation();

watch(internalModelValue, () => {
    props.reactiveValidation ?? v$.value.$touch();
});

const emit = defineEmits<{
    (on: "update:modelValue", value: string | number): void
    (on: "blur"): void
}>();

const name = inject(nameInjectionKey);

if (typeof name !== "string") {
    throw new Error();
}

const defaultInputRef = ref<HTMLInputElement | null>(null);
const currencyInput = props.type === "currency"
    ? useCurrencyInput({
        currency: "GBP",
        currencyDisplay: CurrencyDisplay.hidden,
        precision: 2,
        useGrouping: true,
    }, false)
    : null;

const inputRef = currencyInput?.inputRef ?? defaultInputRef;

function runReactiveValidation() {
    const validationConfig = computed(() => ({
        field: props.validation ?? {},
    }));

    return useVuelidate(validationConfig, {
        field: internalModelValue,
    });
}

function runStaticValidation() {
    return useVuelidate({
        field: props.validation ?? {},
    }, {
        field: internalModelValue,
    });
}
</script>
<template>
    <div :class="props?.class">
        <label
            v-if="hiddenLabel"
            :for="id || name"
            class="visually-hidden-focusable"
        >
            {{ hiddenLabel }}
        </label>
        <label
            v-if="label"
            :for="id || name"
        >
            {{ label }}
        </label>
        <div :class="(prepend || append || props.type === 'currency') ? 'input-group input-wrapper' : ''">
            <span
                v-if="prepend || props.type === 'currency'"
                class="input-group-text"
            >{{ props.type === "currency" ?
                '£' : prepend }}</span>
            <input
                :id="id || name"
                ref="inputRef"
                :type="type"
                :value="modelValue"
                :maxLength="maxLength"
                :class="[
                    {
                        input: true,
                        sessioncamhidetext: hidePii,
                        errorfield: v$.$error,
                    },
                    inputClass
                ]"
                :disabled="disabled"
                class="form-control"
                :placeholder="placeholder || undefined"
                :inputmode="props.inputmode"
                @keydown="props.filter"
                @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
                @blur="v$.$touch(); emitBlurEvent ? emit('blur') : '';"
            >
            <span
                v-if="append"
                id="basic-addon2"
                class="input-group-text"
            >{{ append }}</span>
        </div>
        <div
            v-if="v$.$error"
            class="field-validation-error"
            :class="{ 'error-margin': (prepend || append || props.type === 'currency') }"
        >
            <div class="alert alert-danger">
                {{ v$.$errors[0].$message }}
            </div>
        </div>
    </div>
</template>
<style>
.error-margin {
    margin-top: 6px;
}
</style>
